/**
 * Zeitraumwahl: Modus plus die dazu passende Feinauswahl. Der Zustand steht in der URL
 * (`usePeriodFilter`), damit Reload und geteilter Link denselben Zeitraum zeigen (§5).
 */
import { SegmentedControl, Input, Select } from '@/shared/components';
import type { PeriodMode, PeriodState } from './usePeriodFilter';

interface Props {
  state: PeriodState;
  onChange: (patch: Partial<PeriodState>) => void;
}

const MODES: { value: PeriodMode; label: string }[] = [
  { value: 'year', label: 'Jahr' },
  { value: 'quarter', label: 'Quartal' },
  { value: 'month', label: 'Monat' },
  { value: 'custom', label: 'Frei' },
];

const MONTHS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const YEAR_RANGE = (() => {
  const now = new Date().getUTCFullYear();
  const years: number[] = [];
  for (let year = now - 3; year <= now + 1; year++) years.push(year);
  return years;
})();

export function PeriodFilter({ state, onChange }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        value={state.mode}
        onChange={(value) => onChange({ mode: value as PeriodMode })}
        options={MODES}
      />
      {state.mode !== 'custom' && (
        <Select
          aria-label="Jahr"
          value={state.year}
          onChange={(event) => onChange({ year: Number(event.target.value) })}
          className="w-28! py-1.5! text-sm"
        >
          {YEAR_RANGE.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </Select>
      )}
      {state.mode === 'quarter' && (
        <Select
          aria-label="Quartal"
          value={state.quarter}
          onChange={(event) => onChange({ quarter: Number(event.target.value) })}
          className="w-24! py-1.5! text-sm"
        >
          {[1, 2, 3, 4].map((quarter) => (
            <option key={quarter} value={quarter}>
              Q{quarter}
            </option>
          ))}
        </Select>
      )}
      {state.mode === 'month' && (
        <Select
          aria-label="Monat"
          value={state.month}
          onChange={(event) => onChange({ month: Number(event.target.value) })}
          className="w-32! py-1.5! text-sm"
        >
          {MONTHS_DE.map((month, index) => (
            <option key={month} value={index + 1}>
              {month}
            </option>
          ))}
        </Select>
      )}
      {state.mode === 'custom' && (
        <>
          <Input
            type="date"
            aria-label="Zeitraum von"
            value={state.customFrom}
            onChange={(event) => onChange({ customFrom: event.target.value })}
            className="w-40! py-1.5! text-sm"
          />
          <span className="text-ink-soft text-sm">bis</span>
          <Input
            type="date"
            aria-label="Zeitraum bis"
            value={state.customTo}
            onChange={(event) => onChange({ customTo: event.target.value })}
            className="w-40! py-1.5! text-sm"
          />
        </>
      )}
    </div>
  );
}
